import React from 'react';
import { Landmark, ShieldCheck, AlertTriangle, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Three mutually-exclusive UI states for the payout banner.
const STATE_CONFIRMED = 'confirmed';
const STATE_PENDING = 'pending';
const STATE_UNCONFIGURED = 'unconfigured';

// Object-lookup tables replace nested ternaries for readability/maintenance.
const CONTAINER_CLASSES = {
  [STATE_CONFIRMED]: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  [STATE_PENDING]: 'border-amber-200 bg-amber-50 text-amber-900',
  [STATE_UNCONFIGURED]: 'border-slate-200 bg-slate-50 text-slate-700',
};

const ICON_BY_STATE = {
  [STATE_CONFIRMED]: ShieldCheck,
  [STATE_PENDING]: AlertTriangle,
  [STATE_UNCONFIGURED]: Landmark,
};

const ICON_CLASSES = {
  [STATE_CONFIRMED]: 'text-emerald-700',
  [STATE_PENDING]: 'text-amber-700',
  [STATE_UNCONFIGURED]: 'text-slate-600',
};

const CTA_LABELS = {
  [STATE_CONFIRMED]: 'Manage',
  [STATE_PENDING]: 'Confirm now',
  [STATE_UNCONFIGURED]: 'Set up',
};

const resolveState = (info) => {
  if (info.configured && info.is_confirmed) return STATE_CONFIRMED;
  if (info.configured && !info.is_confirmed) return STATE_PENDING;
  return STATE_UNCONFIGURED;
};

const BannerMessage = ({ state, payoutInfo }) => {
  if (state === STATE_CONFIRMED) {
    return (
      <>
        <strong>Funds deposited to:</strong> {payoutInfo.bank_name}{' '}
        <span className="tabular-nums">····{payoutInfo.last4}</span>{' '}
        <span className="opacity-70">({payoutInfo.country})</span>
      </>
    );
  }
  if (state === STATE_PENDING) {
    return (
      <>
        Payout bank ({payoutInfo.bank_name} ····{payoutInfo.last4}) is set but{' '}
        <strong>awaiting platform-owner confirmation</strong>.
      </>
    );
  }
  return (
    <>
      <strong>Payout bank not configured yet.</strong> Subscriptions will still process; the platform owner needs to set up payout details.
    </>
  );
};

/**
 * "Where subscription money is deposited" banner — visible to every authed
 * user so subscribers can verify the destination. Super-admin gets a CTA to
 * manage / confirm / set up the bank account.
 */
const PayoutBanner = ({ payoutInfo, isSuperAdmin, onManage }) => {
  if (!payoutInfo) return null;

  const state = resolveState(payoutInfo);
  const Icon = ICON_BY_STATE[state];

  return (
    <div
      className={`mb-6 rounded-sm border p-4 flex items-center justify-between gap-4 flex-wrap ${CONTAINER_CLASSES[state]}`}
      data-testid="billing-payout-banner"
    >
      <div className="flex items-center gap-3">
        <Icon size={20} className={`${ICON_CLASSES[state]} flex-shrink-0`} />
        <div className="text-sm font-ibm">
          <BannerMessage state={state} payoutInfo={payoutInfo} />
        </div>
      </div>
      {isSuperAdmin && (
        <Button
          onClick={onManage}
          data-testid="billing-payout-configure-button"
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
        >
          {CTA_LABELS[state]}
          <ArrowUpRight size={14} className="ml-2" />
        </Button>
      )}
    </div>
  );
};

export default PayoutBanner;
